import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Firebase configuration
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);

// Initialize Firestore with offline persistence
// Using new API for Firebase 9+
export const db = initializeFirestore(app, {
    localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
    })
});

export const functions = getFunctions(app);
export const storage = getStorage(app);

/**
 * Emulator gate (Task #14).
 *
 * When `VITE_USE_EMULATOR === 'true'`, redirect Auth + Firestore to the
 * locally-running Firebase Emulator Suite (see `firebase.json` /
 * `npm run emulators`). This is the e2e-only path used by Playwright
 * — Production builds and dev runs against the real project must NEVER
 * set this flag.
 *
 * Hard guard: the flag is only honored when running in the browser AND
 * the hostname is localhost / 127.0.0.1. A misconfigured deploy that
 * accidentally shipped `VITE_USE_EMULATOR=true` would still NOT connect
 * to emulators in production because the hostname check fails.
 */
const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';
const isLocalHost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

if (useEmulator && isLocalHost) {
    // `disableWarnings` suppresses the noisy "you are using the emulator"
    // banner; the e2e suite has its own log.
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    // eslint-disable-next-line no-console
    console.info('[firebase] Connected to local emulators (auth:9099, firestore:8080)');
}


/**
 * Build the canonical Cloud Functions callable URL for a given function
 * name. Use with `httpsCallableFromURL(functions, callableUrl('name'))`.
 * Originally added to bypass Firebase JS SDK v12's protocol negotiation
 * against 1st-gen deployments (OPTIONS preflight CORS rejection). As of
 * Task #29 the fleet is 2nd-gen (firebase-functions v6): gen-2 functions
 * keep the same https://<region>-<project>.cloudfunctions.net/<name> URL
 * in the same region, and v2 onCall handles CORS preflight natively, so
 * this helper continues to work unchanged. Pinned to us-central1 — the
 * region the whole fleet deploys to (see functions/src/index.ts
 * setGlobalOptions). Do not change one without the other.
 */
const FUNCTIONS_REGION = 'us-central1';
export function callableUrl(name: string): string {
    const projectId = firebaseConfig.projectId;
    if (useEmulator && isLocalHost) {
        return `http://127.0.0.1:5001/${projectId}/${FUNCTIONS_REGION}/${name}`;
    }
    return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/${name}`;
}

export default app;
